import { useEffect, useState } from 'react'
import { formatarNumero } from '@viewer/report/documentModel'
import {
  type AnaliseDasPropriedades,
  type UnidadesDoProjecto,
  analisarUnidadesDasPropriedades,
  obterUnidadesDoProjecto,
} from '@viewer/lib/unidadesDoModelo'
import { nomeDoTipo, textoDaOrigem } from '@viewer/lib/unidades'

const CLASSE: Record<string, string> = {
  SI: 'SI',
  conversao: 'por conversão',
  derivada: 'derivada',
  monetaria: 'moeda',
  contexto: 'do contexto',
}

/** As unidades que o projecto declara, e as que as propriedades
 *  declaram. Para responder a duas perguntas: em que unidade está este
 *  modelo, e alguma propriedade usa outra? */
export default function UnidadesDoModelo({ modelId }: { modelId: string }) {
  const [carga, setCarga] = useState<{
    modelId: string
    unidades: UnidadesDoProjecto | null | 'a-ler'
  }>({ modelId, unidades: 'a-ler' })
  const [analise, setAnalise] = useState<{ modelId: string; r: AnaliseDasPropriedades | null } | null>(null)
  const [progresso, setProgresso] = useState<number | null>(null)

  useEffect(() => {
    let vivo = true
    void obterUnidadesDoProjecto(modelId).then((u) => {
      if (vivo) setCarga({ modelId, unidades: u })
    })
    return () => {
      vivo = false
    }
  }, [modelId])

  const unidades = carga.modelId === modelId ? carga.unidades : 'a-ler'
  const analiseAqui = analise?.modelId === modelId ? analise.r : null

  if (unidades === 'a-ler') {
    return <div className="px-1 text-[11px] text-[var(--color-text-muted)]">A ler as unidades do IFC…</div>
  }
  if (unidades === null) {
    return (
      <div className="px-1 text-[11px] text-[var(--color-text-muted)]">
        Sem acesso ao IFC original deste modelo (por exemplo, um .frag): as unidades não se podem ler.
      </div>
    )
  }

  const linhas = [...unidades.porTipo.values()].sort((a, b) =>
    nomeDoTipo(a.tipo).localeCompare(nomeDoTipo(b.tipo), 'pt'),
  )

  return (
    <div className="space-y-2 text-[11px]">
      {unidades.emFalta.length > 0 && (
        <div className="rounded border border-[var(--color-warn-border)] bg-[var(--color-warn-bg)] px-2 py-1.5 text-[var(--color-warn-text)]">
          O projecto não declara unidade de {unidades.emFalta.map(nomeDoTipo).join(', ')}. Os valores dessas
          medidas sem unidade própria ficam sem unidade definida.
        </div>
      )}
      {unidades.ambiguas.size > 0 && (
        <div className="rounded border border-[var(--color-error-border)] bg-[var(--color-error-bg)] px-2 py-1.5 text-[var(--color-error-text)]">
          O projecto declara mais de uma unidade para o mesmo tipo, o que o IFC não permite (IfcUnitAssignment,
          regra WR01). Nesses tipos a unidade não se pode determinar, e não se escolhe nenhuma:
          <ul className="list-disc pl-4">
            {[...unidades.ambiguas].map(([tipo, lista]) => (
              <li key={tipo}>
                {nomeDoTipo(tipo)}: {lista.map((u) => `${u.simbolo}${u.origem ? ` em #${u.origem.id}` : ''}`).join(', ')}
              </li>
            ))}
          </ul>
        </div>
      )}
      <div>
        <div className="mb-1 text-[10px] uppercase tracking-wide text-[var(--color-text-muted)]">
          Unidades do projecto ({formatarNumero(linhas.length)})
        </div>
        {linhas.length === 0 ? (
          <div className="text-[var(--color-warn-text)]">O projecto não declara unidade nenhuma.</div>
        ) : (
          <table className="w-full border-collapse">
            <tbody>
              {linhas.map((u) => (
                <tr key={u.tipo} className="border-b border-[var(--color-border)]/40 last:border-b-0">
                  <td className="py-0.5 pr-2 text-[var(--color-text-muted)]">{nomeDoTipo(u.tipo)}</td>
                  <td className="py-0.5 pr-2 font-mono text-[var(--color-text)]" title={u.nome}>
                    {u.simbolo}
                    {u.equivalencia && (
                      <span
                        className="ml-1 font-sans text-[10px] text-[var(--color-text-muted)]"
                        title={`Calculado a partir das unidades de base. O ficheiro declara ${u.simbolo}; ${u.equivalencia} é a mesma unidade escrita da forma habitual.`}
                      >
                        = {u.equivalencia} <span className="italic">(calculado)</span>
                      </span>
                    )}
                  </td>
                  <td className="py-0.5 pr-2 text-[10px] text-[var(--color-text-dim)]" title={u.detalhe}>
                    {u.classe === 'conversao' && u.detalhe ? u.detalhe : CLASSE[u.classe]}
                  </td>
                  <td
                    className="py-0.5 whitespace-nowrap font-mono text-[10px] text-[var(--color-text-dim)]"
                    title="A entidade no ficheiro IFC de onde esta unidade foi lida."
                  >
                    {textoDaOrigem(u)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div>
        <div className="mb-1 text-[10px] uppercase tracking-wide text-[var(--color-text-muted)]">
          Unidades nas propriedades
        </div>
        {!analiseAqui ? (
          <button
            type="button"
            disabled={progresso !== null}
            onClick={() => {
              setProgresso(0)
              void analisarUnidadesDasPropriedades(modelId, (f, t) => setProgresso(t ? f / t : 1))
                .then((r) => setAnalise({ modelId, r }))
                .finally(() => setProgresso(null))
            }}
            className="rounded border border-[var(--color-border)] px-2 py-0.5 hover:text-[var(--color-text)] disabled:opacity-50"
            title="Percorre todas as propriedades e quantidades do modelo e verifica quais declaram a sua própria unidade."
          >
            {progresso !== null ? `A analisar… ${Math.round(progresso * 100)}%` : 'Analisar as propriedades'}
          </button>
        ) : (
          <div className="space-y-1">
            <p className="text-[var(--color-text-muted)]">
              {formatarNumero(analiseAqui.total)} propriedades e quantidades;{' '}
              {formatarNumero(analiseAqui.comDimensao)} com valor com dimensão, das quais{' '}
              {formatarNumero(analiseAqui.comUnidadePropria)} declaram unidade própria.
              {analiseAqui.comDimensao > 0 &&
                analiseAqui.comUnidadePropria === 0 &&
                ' Todas usam as unidades do projecto.'}
            </p>
            {analiseAqui.porUnidade.length > 0 && (
              <table className="w-full border-collapse">
                <tbody>
                  {analiseAqui.porUnidade.map((u) => (
                    <tr key={`${u.unidade.tipo}|${u.unidade.simbolo}`} className="border-b border-[var(--color-border)]/40 last:border-b-0">
                      <td className="py-0.5 pr-2 font-mono text-[var(--color-accent)]" title={textoDaOrigem(u.unidade) ? `No ficheiro: ${textoDaOrigem(u.unidade)}` : undefined}>
                        {u.unidade.simbolo}
                        {u.unidade.equivalencia && (
                          <span className="ml-1 font-sans text-[10px] text-[var(--color-text-muted)]">= {u.unidade.equivalencia} <span className="italic">(calculado)</span></span>
                        )}
                      </td>
                      <td className="py-0.5 pr-2 text-[var(--color-text-muted)]">{nomeDoTipo(u.unidade.tipo)}</td>
                      <td className="py-0.5 pr-2 text-right">{formatarNumero(u.n)}</td>
                      <td className="py-0.5 text-[10px] text-[var(--color-text-dim)]">{u.exemplos.join(', ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {analiseAqui.semUnidade.length > 0 && (
              <div className="rounded border border-[var(--color-warn-border)] bg-[var(--color-warn-bg)] px-2 py-1.5 text-[var(--color-warn-text)]">
                Sem unidade definida (nem na propriedade nem no projecto):
                <ul className="list-disc pl-4">
                  {analiseAqui.semUnidade.map((s) => (
                    <li key={s.tipo}>
                      {nomeDoTipo(s.tipo)}: {formatarNumero(s.n)} ({s.exemplos.join(', ')})
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
